import { 
    Badge,
    Chip,
    CircularProgress,
    createStyles,
    IconButton,
    InputAdornment,
    lighten,
    makeStyles, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    TextField, 
    Theme, 
    Toolbar,
    Tooltip,
    Typography
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
// tslint:disable-next-line: max-line-length
import { 
    DragDropContext, 
    Draggable, 
    // DraggableProvided, 
    // DraggableStateSnapshot, 
    Droppable, 
    DropResult, 
    ResponderProvided 
} from 'react-beautiful-dnd';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import AddBoxRoundedIcon from '@material-ui/icons/AddBoxRounded';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import Check from '@material-ui/icons/Check';
import { mainTheme } from '../../Theme';
import _ from 'lodash';
import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import { client } from '../..';
import { showNotification } from '../../App';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import { browserHistory } from '../../components/AppContent';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
            position: 'relative'
        },
        loadingWrapper: {
            position: 'absolute',
            zIndex: 1,
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255, 0.6)',
        },
        icon: {
            color: mainTheme.BrandColors.TemplatePrimary,
        },
        tableCell: {
            paddingTop: 0,
            paddingBottom: 0,
            '&:hover': {
                '& .drag-icon': {
                    color: mainTheme.BrandColors.TemplatePrimary
                }
            }
        },
        textField: {
            minWidth: 200,
            maxWidth: '100%',
        },
        dragIcon: {
            marginLeft: '-10px',
            marginRight: 10,
            color: '#BDBDBD'
        },
        tableContentWrapper: {
            display: 'flex',
            alignItems: 'center',
        },
        description: {
            cursor: 'pointer'
        }
    })
);

const useToolbarStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(1),
            display: 'flex',
            justifyContent: 'space-between'
        },
        highlight:
        theme.palette.type === 'light'
            ? {
                color: theme.palette.secondary.main,
                backgroundColor: lighten(theme.palette.secondary.light, 0.85),
            }
            : {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.secondary.dark,
            },
        title: {
            flex: '1 1 100%',
        },
        icon: {
            color: mainTheme.BrandColors.TemplatePrimary,
        },
        rightToolbarWrapper: {
            display: 'flex',
            alignItems: 'center'
        },
        tableTitle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
        },
        tableHeading: {
            color: mainTheme.BrandColors.TemplatePrimary
        },
        chip: {
            marginLeft: '8px'
        },
    })
);

interface EnhancedTableToolbarProps {
    title: string;
    count?: React.ReactNode;
    onClickAdd?: () => void;
    onSearch?: (search: string) => void;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {

    const classes = useToolbarStyles();

    const [search, setSearch] = useState('');

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        
        setSearch(event.target.value);

        if (props.onSearch) {
            props.onSearch(event.target.value);
        }
    };

    const clearSearch = () => {
        setSearch('');
        if (props.onSearch) {
            props.onSearch('');
        }
    };

    return (
        <Toolbar
            className={classes.root}
        >
            <div className={classes.tableTitle}>
                <Typography className={classes.tableHeading}>{props.title}</Typography>
                <Chip className={classes.chip} label={props.count} />
            </div>
            <div className={classes.rightToolbarWrapper}>
                <TextField
                    // className={classes.margin}
                    placeholder="Search"
                    value={search}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" className={classes.icon} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="start">
                                <IconButton
                                    onClick={clearSearch}
                                    size="small"
                                >
                                    <CloseIcon fontSize="small" className={classes.icon} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    onChange={handleChange}
                />
                <Tooltip title="Add">
                    <IconButton 
                        aria-label="filter list"
                        onClick={props.onClickAdd}
                    >
                        <AddBoxRoundedIcon className={classes.icon}/>
                    </IconButton>
                </Tooltip>
            </div>
        </Toolbar>
    );
};

interface LookupNameTagTypeMaintenanceItem extends ClientAndContactTagType {
    isEdit: boolean;
    isDelete: boolean;
}

interface LookupNameTagTypeMaintenanceState {
    items: LookupNameTagTypeMaintenanceItem[];
    isAdd: boolean;
    search: string;
    isDelete: boolean;
    isEdit: boolean;
}

export default function LookupNameTagTypeMaintenance() {

    const classes = useStyles();

    const { loading, error, data } = useQuery<ClientAndContactTagTypeData>(ClientAndContactTagTypesQuery);

    const [state, setState] = useState<LookupNameTagTypeMaintenanceState>({
        items: [],
        isAdd: false,
        search: '',
        isEdit: false,
        isDelete: false,
    });

    const textFieldPlaceHolder = 'Description';

    useEffect(() => {
        if (!error && data && data.lookup ) {
            setState((prevState) => {
                return {
                    ...prevState,
                    items: data!.lookup.clientAndContactTagType.map((value) => {
                        return { 
                            guidId: value.guidId,
                            description: value.description,
                            sequence: value.sequence,
                            tagEntity: value.tagEntity,
                            tagsCount: value.tagsCount,
                            isDelete: false,
                            isEdit: false
                        };
                    }).sort((a, b) => a.sequence < b.sequence ? -1 : a.sequence > b.sequence ? 1 : 0)
                };
            });
        }
    // tslint:disable-next-line: align
    }, [data, error, loading]);

    useEffect(() => {
        
        const subscribeFirmObj = subscribeFirm();

        return () => {
            subscribeFirmObj.unsubscribe();
        };
    // tslint:disable-next-line: align
    }, []);

    const subscribeFirm = () => {
        return (
            // let subscription =
            client
                .subscribe({
                    fetchPolicy: 'no-cache',
                    query: firmSubscription,
                    variables: {},
                })
                .subscribe({
                    // tslint:disable-next-line:no-any
                    next(results: any) {
                        if (results.data.lookupNotification.status) {

                            const { lookupNotification } = results.data;

                            showNotification('Success', lookupNotification.message, 'info');

                            if (lookupNotification.message.includes('created successfully')) {
                                
                                setState((prevState) => {

                                    const tdata = [...prevState.items];

                                    const temp = tdata[tdata.length - 1];

                                    tdata[tdata.length - 1] = {
                                        ...temp,
                                        guidId: lookupNotification.guidId
                                    };

                                    return {
                                        ...prevState,
                                        items: tdata
                                    };
                                });
                            }

                        } else {
                            showNotification('Failed', results.data.lookupNotification.message, 'error');
                        }
                    },
                    // tslint:disable-next-line:no-any
                    error(err: any) {
                        // tslint:disable-next-line:no-console
                        console.error(err);
                        showNotification(null, err, 'error');
                    },
                })
        );
    };
    
    let textField = '';

    // itemInputRef.current = getItems(10).map((ref, index) => itemInputRef.current[index] = React.createRef());

    const reorder = (list: LookupNameTagTypeMaintenanceItem[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
      
        return result;
    };

    const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
    
        const items = reorder(
            state.items,
            result.source.index,
            result.destination.index
        ).map((item: LookupNameTagTypeMaintenanceItem, index: number) => {
            return {
                ...item,
                sequence: index
            };
        });

        setState((prevState) => {
            
            client
            .mutate({
                mutation: UpdateClientAndContactTagTypeSequences,
                variables: {
                    lookupSequences: items.map((item: LookupNameTagTypeMaintenanceItem) => {
                        return {
                            guidId: item.guidId, 
                            sequence: item.sequence
                        };
                    })
                },
            })
            // tslint:disable-next-line: no-any
            .then((results: { data: any }) => {
                if (results.data) {
                    if (results.data.error !== undefined) {
                        showNotification( 'Failed to update tag type sorting', results.data.error, 'error');
                    }
                }
            })
            // tslint:disable-next-line:no-any
            .catch((reason: any) => {
                showNotification('Failed to update tag type sorting', reason, 'error');
            });

            return {
                ...prevState,
                items
            };
        });

    };

    // const getListStyle = (isDraggingOver: boolean) => ({
    //     background: isDraggingOver ? 'lightblue' : 'lightgrey',
    //     padding: grid,
    //     width: 250
    // });

    // tslint:disable-next-line: no-any
    const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
        // styles we need to apply on draggables
        ...draggableStyle,

        ...(isDragging && {
            background: 'rgb(235,235,235)'
        }),
        position: 'static',
        zIndex: 1
    });

    const onClickAdd = () => {
        setState((prevState) => {
            return {
                ...prevState,
                isAdd: true
            };
        });
    };

    const onClickCancel = () => {
        setState((prevState) => {
            return {
                ...prevState,
                isAdd: false,
                isEdit: false,
                isDelete: false,
            };
        });
    };

    const onSearch = (search: string) => {
        setState((prevState) => {
            return {
                ...prevState,
                search
            };
        });
    };

    const onEditRowEnable = (item: LookupNameTagTypeMaintenanceItem) => () => {
        const items = [...state.items];
        
        const index = items.indexOf(item);

        items[index].isEdit = true;

        setState((prevState) => {
            return {
                ...prevState,
                items,
                isEdit: true,
            };
        });
    };

    const onCancelEditRow = (item: LookupNameTagTypeMaintenanceItem) => () => {
        // code here..
        const items = [...state.items];
        
        const index = items.indexOf(item);

        items[index].isEdit = false;

        setState((prevState) => {
            return {
                ...prevState,
                items,
                isEdit: false,
            };
        });
    };

    const onEditRow = (item: LookupNameTagTypeMaintenanceItem, index: number) => () => {

        if (textField) {

            setState((prevState) => {
                client
                .mutate({
                    mutation: UpdateClientAndContactTagTypeMutation,
                    variables: {
                        guidId: item.guidId,
                        description: textField,
                    },
                })
                // tslint:disable-next-line: no-any
                .then((results: { data: any }) => {
                    if (results.data) {
                        if (results.data.error !== undefined) {
                            showNotification( 'Failed to Update Tag Type', results.data.error, 'error');
                        }
                    }
                })
                // tslint:disable-next-line:no-any
                .catch((reason: any) => {
                    showNotification('Failed to Update Tag Type', reason, 'error');
                });

                const tdata = [...prevState.items];
                tdata[tdata.indexOf(item)].isEdit = false;
                tdata[tdata.indexOf(item)].description = textField;
                
                return { 
                    ...prevState, 
                    data: tdata,
                    isEdit: false
                };
            });
        }
    };

    const onDeleteRowEnable = (item: LookupNameTagTypeMaintenanceItem) => () => {

        setState((prevState) => {

            const items = [...prevState.items];
        
            const index = items.indexOf(item);

            items[index].isDelete = true;

            return {
                ...prevState,
                items,
                isDelete: true
            };
        });
    };

    const onCancelDeleteRow = (item: LookupNameTagTypeMaintenanceItem) => () => {
        // code here..

        setState((prevState) => {

            const items = [...prevState.items];
        
            const index = items.indexOf(item);

            items[index].isDelete = false;

            return {
                ...prevState,
                items,
                isDelete: false
            };
        });
    };

    const onDeleteRow = (item: LookupNameTagTypeMaintenanceItem) => () => {
        
        setState((prevState) => {

            client
            .mutate({
                mutation: DeleteClientAndContactTagTypeMutation,
                variables: {
                    guidId: item.guidId,
                    description: item.description
                },
            })
            // tslint:disable-next-line: no-any
            .then((results: { data: any }) => {
                if (results.data) {
                    if (results.data.error !== undefined) {
                        showNotification( 'Failed to Delete Tag Type', results.data.error, 'error');
                    }
                }
            })
            // tslint:disable-next-line:no-any
            .catch((reason: any) => {
                showNotification('Failed to Delete Tag Type', reason, 'error');
            });

            const tItems = [...prevState.items];
            tItems.splice(tItems.indexOf(item), 1);

            return {
                ...prevState,
                isDelete: false,
                items: tItems
            };
        });
    };

    const onAddItem = () => {
        
        if (textField) {

            setState((prevState) => {

                client
                .mutate({
                    mutation: CreateClientAndContactTagTypeMutation,
                    variables: {
                        description: textField,
                        sequence: prevState.items.length,
                    },
                })
                // tslint:disable-next-line: no-any
                .then((results: { data: any }) => {
                    if (results.data) {
                        if (results.data.error !== undefined) {
                            showNotification( 'Failed to create Tag Type', results.data.error, 'error');
                        }
                    }
                })
                // tslint:disable-next-line:no-any
                .catch((reason: any) => {
                    showNotification('Failed to create Tag Type', reason, 'error');
                });

                const tItems = [...prevState.items];
                
                tItems.push({
                    guidId: '',
                    description: textField,
                    sequence: prevState.items.length,
                    isEdit: false,
                    isDelete: false,
                    tagEntity: 0,
                    tagsCount: 0,
                });
                
                return {
                    ...prevState,
                    isAdd: false,
                    items: tItems
                };
            });
        }
    
    };

    const onClickTagCounts = (item: LookupNameTagTypeMaintenanceItem) => () => {
        browserHistory.push(`/lookupConfiguration/nameTagTypes/${item.description};key=${item.guidId}`);
    };

    const onChangeUpdate = (item: LookupNameTagTypeMaintenanceItem, index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        textField = event.target.value;
    };

    const onChangeAdd = (event: React.ChangeEvent<HTMLInputElement>) => {
        textField = event.target.value;
    };

    // tslint:disable-next-line: no-any
    const DroppableComponent = (onDragEndComponent: (result: DropResult, provided: ResponderProvided) => void) => (props: any) =>  {
        return (
            <DragDropContext onDragEnd={onDragEndComponent}>
                <Droppable droppableId={'1'} direction="vertical">
                    {(provided) => {
                        return (
                            <TableBody ref={provided.innerRef} {...provided.droppableProps} {...props}>
                                {props.children}
                                {provided.placeholder}
                            </TableBody>
                        );
                    }}
                </Droppable>
            </DragDropContext>
        );
    };

    // tslint:disable-next-line: no-any
    const DraggableComponent = (item: LookupNameTagTypeMaintenanceItem, index: number) => (props: any) => {
        return (
            <Draggable draggableId={item.guidId} index={index}>
                {(provided, snapshot) => (
                    <TableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
    
                        {...props}
                    >
                        {!!(!item.isDelete && !item.isEdit)  && (
                             <>
                                <TableCell className={classes.tableCell}>
                                    <div className={classes.tableContentWrapper}>
                                        <DragIndicatorIcon fontSize="small" className={`${classes.dragIcon} drag-icon`} />
                                        <div
                                            onClick={onClickTagCounts(item)}
                                            className={classes.description}
                                        >
                                            {item.description}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className={classes.tableCell} align="center">
                                    <IconButton
                                        onClick={onClickTagCounts(item)}
                                    >
                                        <Badge color="primary" badgeContent={item.tagsCount}>
                                            <LocalOfferOutlinedIcon color="primary" />
                                        </Badge>
                                    </IconButton>
                                </TableCell>
                                <TableCell className={classes.tableCell} align="right">
                                    <IconButton 
                                        aria-label="Edit"
                                        disabled={state.isAdd || state.isEdit || state.isDelete}
                                        color="primary"
                                        onClick={onEditRowEnable(item)}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton 
                                        aria-label="Delete"
                                        disabled={state.isAdd || state.isEdit || state.isDelete}
                                        color="primary"
                                        onClick={onDeleteRowEnable(item)}
                                    >
                                        <DeleteOutline />
                                    </IconButton>
                                </TableCell>
                            </>
                        )}

                        {!!(item.isDelete && state.isDelete) && (
                            <>
                                <TableCell className={classes.tableCell} colSpan={2}>
                                    Are you sure you want to delete this row?
                                </TableCell>
                                <TableCell className={classes.tableCell} align="right">
                                    <IconButton 
                                        aria-label="ok"
                                        // disabled={state.isAdd}
                                        color="primary"
                                        onClick={onDeleteRow(item)}
                                    >
                                        <Check />
                                    </IconButton>
                                    <IconButton 
                                        aria-label="cancel"
                                        // disabled={state.isAdd}
                                        color="primary"
                                        onClick={onCancelDeleteRow(item)}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </TableCell>
                            </>
                        )}
                        {!!(item.isEdit && state.isEdit) && (
                            <>
                                <TableCell className={classes.tableCell} colSpan={2}>
                                    <TextField 
                                        placeholder={textFieldPlaceHolder}
                                        defaultValue={item.description}
                                        onChange={onChangeUpdate(item, index)}
                                        className={classes.textField}
                                    />
                                </TableCell>
                                <TableCell className={classes.tableCell} align="right">
                                    <IconButton 
                                        aria-label="ok"
                                        disabled={state.isAdd}
                                        color="primary"
                                        onClick={onEditRow(item, index)}
                                    >
                                        <Check />
                                    </IconButton>
                                    <IconButton 
                                        aria-label="cancel"
                                        disabled={state.isAdd}
                                        color="primary"
                                        onClick={onCancelEditRow(item)}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </TableCell>
                            </>
                        )}
                        
                    </TableRow>
                )}
            </Draggable>
        );
    };

    const getDraggableRows = () => {
        let filteredItems = state.items;
        
        if (state.search) {
            filteredItems = state.items.filter((item: LookupNameTagTypeMaintenanceItem) => {
                return item.description.toLowerCase().indexOf(state.search) >= 0;
            });
        }       

        return (
            _.map(filteredItems, (item, index) => {
                return (
                    <TableRow 
                        component={DraggableComponent(item, index)} 
                        key={item.guidId}
                    />
                );
            })
        );
    };

    return (
        <div className={`${classes.root}`}>
            <div className={classes.paper}>
                {!!loading && (
                    <div className={classes.loadingWrapper}>
                        <CircularProgress />
                    </div>
                )}
                <EnhancedTableToolbar 
                    title="Tag Type"
                    count={state.items.length}
                    onClickAdd={onClickAdd}
                    onSearch={onSearch}
                />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell align="center">Tags Count</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody component={DroppableComponent(onDragEnd)}>
                            {!!state.isAdd && (
                                <TableRow>
                                    <TableCell className={classes.tableCell} colSpan={2}>
                                        <TextField
                                            placeholder={textFieldPlaceHolder}
                                            className={classes.textField}
                                            onChange={onChangeAdd}
                                        />
                                    </TableCell>
                                    <TableCell className={classes.tableCell} align="right">
                                        <Tooltip title="Save">
                                            <IconButton 
                                                aria-label="Save"
                                                onClick={onAddItem}
                                            >
                                                <Check className={classes.icon}/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Cancel">
                                            <IconButton 
                                                aria-label="Delete"
                                                onClick={onClickCancel}
                                            >
                                                <CloseIcon className={classes.icon}/>
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )}
                            {
                                getDraggableRows()
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export interface ClientAndContactTagTypeData {
    loading?: boolean;
    networkStatus?: number;
    lookup: Lookup;
}

export interface Lookup {
    clientAndContactTagType: ClientAndContactTagType[];
}

export interface ClientAndContactTagType {
    guidId:      string;
    description: string;
    tagEntity:   number;
    tagsCount: number;
    sequence: number;
}

const ClientAndContactTagTypesQuery = gql`
    query ClientAndContactTagTypes {
        lookup {
            clientAndContactTagType {
                guidId
                description
                tagEntity
                tagsCount
                sequence
            }
        }
    }
`;

const CreateClientAndContactTagTypeMutation = gql`
    mutation CreateClientAndContactTagType($description: String, $sequence: Int) {
        createClientAndContactTagType(description: $description, sequence: $sequence)
    }
`;

const UpdateClientAndContactTagTypeMutation = gql`
    mutation UpdateClientAndContactTagType($guidId: String, $description: String) {
        updateClientAndContactTagType(guidId: $guidId, description: $description)
    }
`;

const DeleteClientAndContactTagTypeMutation = gql`
    mutation DeleteClientAndContactTagType($guidId: String, $description: String) {
        deleteClientAndContactTagType(guidId: $guidId, description: $description)
    }
    
`;

const UpdateClientAndContactTagTypeSequences = gql`
    mutation UpdateClientAndContactTagTypeSequences($lookupSequences: [LookupSequence]) {
        updateClientAndContactTagTypeSequence(lookupSequences: $lookupSequences)
    }
`;

export const firmSubscription = gql`
    subscription firm {
        lookupNotification{
            id,
            guidId,
            lookupDescription,
            message,
            status
        }
    }
`;